import React from 'react';
import styled from 'styled-components';
import { Grid } from '../../../app/StyleGuide';
import { useOrg } from '../../org';
import { sanitizeHTML } from '../../../utils/sanitizeHTML';
import useSessionTimeline from '../hooks/useSessionTimeline';
import Agenda from './Agenda';
import {
  Chip,
  PrintColors,
  PrintHeaderSection,
  PrintIcon,
  PrintSubTitle,
  PrintTransition,
  SegmentHeaderContainer,
  PrintMediaSlideContainer,
  VideoSectionContainer,
  FONT_SIZE_SCALE,
  BodyContentContainer,
} from './PrintElements';
import PrintHeader from './PrintHeader';
import Script from './Script';
import Supplies from './Supplies';
import Tip from './Tip';
import { getMediaTitle } from '../timeline/SessionTimelineContent';
import SectionMediaThumbnail from '../timeline/SectionMediaThumbnail';
import { Section, Segment } from '../models/timeline';
import sessionTimelineService from '../../../services/sessionTimelineService';
import Note from './Note';

export type PrintPreferences = {
  viewPreference: string;
  fontSize: keyof typeof FONT_SIZE_SCALE;
  printStyle: 'color' | 'blackAndWhite';
  includeAgenda: boolean;
  media: boolean;
  suppliesOnly: boolean;
};

const showMedia = (section: Section, printPreferences: PrintPreferences) => {
  return (!!section.media || section._type === 'video') && printPreferences.media;
};

const shouldShowSegment = (segment: Segment, viewPreference: string) => {
  return !viewPreference || sessionTimelineService.matchesLeaderViewPreference(viewPreference, segment.leader);
};

const shouldShowSection = (section: Section, printPreferences: PrintPreferences) => {
  if (printPreferences.suppliesOnly) {
    return section._type === 'supplies';
  }
  return true;
};

const PrintContainer = styled.div<{ fontSize: keyof typeof FONT_SIZE_SCALE; printStyle: 'color' | 'blackAndWhite' }>`
  ${`@import url('https://fonts.googleapis.com/css2?family=Schibsted+Grotesk:ital,wght@0,400..900;1,400..900&family=Ubuntu:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700&display=swap');`}
  display: none;

  @media print {
    display: block;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    ${({ printStyle }) =>
      printStyle === 'blackAndWhite' &&
      `
        -webkit-filter: grayscale(100%) url('#print-grayscale') !important;
        filter: grayscale(100%) url('#print-grayscale') !important;
      `}
  }
  font-family: 'Schibsted Grotesk', sans-serif;

  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin: 0;
    font-family: 'Ubuntu', sans-serif !important;
  }

  p {
    margin: 0;
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].base};
    font-family: 'Ubuntu', sans-serif !important;
  }
`;

const SectionContainer = styled.div<{ includeMedia: boolean }>`
  margin-bottom: ${Grid._3};
  margin-left: ${Grid._3};
  margin-right: ${Grid._3};

  display: flex;
  flex-direction: column;
  gap: ${Grid._3};

  ${({ includeMedia }) =>
    includeMedia &&
    `
      flex-direction: row;
    `}
`;

const PrintMediaSlideItem = styled(SectionMediaThumbnail)`
  margin: 0;
`;

const SegmentGroupIconMap = Object.freeze({
  leaders: 'fas fa-chalkboard-teacher',
  small: 'fas fa-user-friends',
  large: 'fas fa-users',
});

export default function PrintableTimeline({ printPreferences }: { printPreferences: PrintPreferences }) {
  const { org } = useOrg();
  const { timeline, sectionTypeMap, segmentGroupMap, segmentLeaderMap } = useSessionTimeline();

  const showGroupHeader = (segment: Segment, i: number, printPreferences: PrintPreferences) => {
    if (i === 0 && segment.sections.filter(section => shouldShowSection(section, printPreferences)).length > 0)
      return true; // First segment always shows header unless all segments are hidden
    if (i === 0) return false; // First segment with no visible sections shouldn't show header
    return segment.group !== timeline.segments[i - 1].group;
  };

  return (
    <PrintContainer fontSize={printPreferences.fontSize} printStyle={printPreferences.printStyle}>
      {printPreferences.printStyle === 'blackAndWhite' && (
        <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true" focusable="false">
          <filter id="print-grayscale">
            <feColorMatrix
              type="matrix"
              values="0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0 0 0 1 0"
            />
          </filter>
        </svg>
      )}
      <PrintHeader
        orgName={org?.name ?? ''}
        fontSize={printPreferences.fontSize}
        includeHeaderImage={printPreferences.media && !printPreferences.suppliesOnly}
      />

      {printPreferences.includeAgenda && !printPreferences.suppliesOnly && (
        <Agenda segments={timeline.segments} fontSize={printPreferences.fontSize} />
      )}

      {!!timeline.segments.length &&
        timeline.segments.map((segment, i) => (
          <React.Fragment key={segment.segmentId}>
            {showGroupHeader(segment, i, printPreferences) && (
              <PrintTransition>
                <SegmentHeaderContainer>
                  <PrintIcon
                    fontSize={printPreferences.fontSize}
                    color={PrintColors.LightGray}
                    className={SegmentGroupIconMap[segment.group as keyof typeof SegmentGroupIconMap]}
                  />
                  <PrintSubTitle fontSize={printPreferences.fontSize}>
                    {segmentGroupMap[segment.group as keyof typeof segmentGroupMap]}
                  </PrintSubTitle>
                </SegmentHeaderContainer>
              </PrintTransition>
            )}
            {shouldShowSegment(segment, printPreferences.viewPreference) && (
              <>
                {segment.sections.filter(section => shouldShowSection(section, printPreferences)).length > 0 && (
                  <PrintHeaderSection>
                    <SegmentHeaderContainer>
                      <PrintSubTitle fontSize={printPreferences.fontSize}>
                        {printPreferences.suppliesOnly ? '' : i + 1 + '. '} {segment.name}{' '}
                        <span> / {segmentLeaderMap[segment.leader as keyof typeof segmentLeaderMap]}</span>
                      </PrintSubTitle>
                    </SegmentHeaderContainer>
                    {segment.duration > 0 ? (
                      <Chip color={PrintColors.LightGreen} fontSize={printPreferences.fontSize}>
                        <p>{segment.duration} minutes</p>
                      </Chip>
                    ) : (
                      <div />
                    )}
                  </PrintHeaderSection>
                )}
                {segment.note && !printPreferences.suppliesOnly && (
                  <Note title={segment.note.title} text={segment.note.body} fontSize={printPreferences.fontSize} />
                )}
                {segment.sections
                  .filter(section => shouldShowSection(section, printPreferences))
                  .map(section => (
                    <SectionContainer key={section.id} includeMedia={showMedia(section, printPreferences)}>
                      {section._type === 'tip' && (
                        <Tip
                          text={section.text ?? ''}
                          tipType={
                            sectionTypeMap.tip.tipType[section.tipType as keyof typeof sectionTypeMap.tip.tipType]
                          }
                          fontSize={printPreferences.fontSize}
                        />
                      )}
                      {section._type === 'video' && (
                        <VideoSectionContainer fontSize={printPreferences.fontSize}>
                          <i className={sectionTypeMap['video'].iconClass}></i>
                          <h5>Presentation</h5>
                          <p>Play {getMediaTitle(section, segment)}</p>
                        </VideoSectionContainer>
                      )}
                      {section._type === 'body' && (
                        <BodyContentContainer fontSize={printPreferences.fontSize}>
                          <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(section.text ?? '') }} />
                        </BodyContentContainer>
                      )}
                      {section._type === 'supplies' && (
                        <Supplies supplies={section.supplies ?? []} fontSize={printPreferences.fontSize} />
                      )}
                      {section._type === 'script' && (
                        <Script text={section.text ?? ''} fontSize={printPreferences.fontSize} />
                      )}
                      {showMedia(section, printPreferences) && (
                        <PrintMediaSlideContainer>
                          <PrintMediaSlideItem
                            media={section.media}
                            title={getMediaTitle(section, segment)}
                            onClick={() => {}}
                            viewOnly
                          />
                        </PrintMediaSlideContainer>
                      )}
                    </SectionContainer>
                  ))}
              </>
            )}
          </React.Fragment>
        ))}
    </PrintContainer>
  );
}
